import { find, get, isEmpty, keys, upperCase } from 'lodash'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import styled from 'styled-components'

import Button from '../Button'
import Popover from '../Popover'
import { useTranslation } from '../Translations'
import { LanguageContext } from './helpers'
import { LanguageSelectComponent } from './LanguageSelectComponent'

type TLanguageSelect = {
  flagsOnly?: boolean
  useShortCode?: boolean
  hideCountryText?: boolean
  enableBlandMode?: boolean
  useDarkMode?: boolean
  flipLeft?: boolean
  useMinWidth?: boolean
  flagSize?: number | string
  skipListener?: boolean
}

const DropdownWrapper = styled.div`
  &.ui.bottom.center.popup:before {
    // display: none; //hide triagle top
  }
`

const DropdownItemWrapper = styled.div`
  cursor: pointer;
  &:hover {
    border-radius: 0.28571429rem;
  }
`

export const LANGUAGE_OPTIONS = [
  { label: 'Deutsch', value: 'de', flag: 'de' },
  { label: 'English', value: 'en', flag: 'gb' },
  { label: 'Français', value: 'fr', flag: 'fr' },
  { label: 'Italiano', value: 'it', flag: 'it' },
  { label: 'Nederlands', value: 'nl', flag: 'nl' },
  { label: 'Polski', value: 'pl', flag: 'pl' },
  { label: 'Русский', value: 'ru', flag: 'ru' },
  { label: 'Türkçe', value: 'tr', flag: 'tr' },
  { label: 'Hrvatski', value: 'hr', flag: 'hr' },
  { label: 'Español', value: 'es', flag: 'es' },
  { label: 'Português', value: 'pt', flag: 'pt' },
  { label: 'Svenska', value: 'sv', flag: 'se' },
  { label: 'Norsk', value: 'no', flag: 'no' },
  { label: 'Dansk', value: 'da', flag: 'dk' },
  { label: 'Suomi', value: 'fi', flag: 'fi' },
  { label: 'Ελληνικά', value: 'el', flag: 'gr' },
  { label: 'Magyar', value: 'hu', flag: 'hu' },
  { label: 'Română', value: 'ro', flag: 'ro' },
  { label: 'Čeština', value: 'cs', flag: 'cz' },
  { label: 'Slovenčina', value: 'sk', flag: 'sk' },
  { label: 'Lietuvių', value: 'lt', flag: 'lt' },
  { label: 'Latviešu', value: 'lv', flag: 'lv' },
  { label: 'Eesti', value: 'et', flag: 'ee' },
  { label: 'Български', value: 'bg', flag: 'bg' },
  { label: 'Shqip', value: 'sq', flag: 'al' },
  { label: 'Македонски', value: 'mk', flag: 'mk' },
  { label: 'Crnogorski', value: 'sr', flag: 'me' }, // Used 'sr' for lack of a specific Montenegrin code
  { label: 'Bosanski', value: 'bs', flag: 'ba' },
  { label: 'Slovenščina', value: 'sl', flag: 'si' },
  { label: 'Română', value: 'ro', flag: 'md' },
  { label: 'Беларуская', value: 'be', flag: 'by' },
  { label: 'Українська', value: 'uk', flag: 'ua' },
]

const LanguageSelect = (props: TLanguageSelect): React.ReactElement => {
  const {
    flagsOnly,
    useShortCode,
    hideCountryText,
    enableBlandMode = false,
    useDarkMode,
    flipLeft = false,
    useMinWidth = false,
    flagSize = 14,
    skipListener = false,
  } = props

  const {
    value: contextValue,
    languages: contextLanguages,
    onChange: contextOnChange,
  } = useContext(LanguageContext)

  const { languages: translatedLanguages = [] } = useTranslation()

  const [value, setValue] = useState<string>()

  const [languages, setLanguages] = useState<string[]>()

  const [openPopup, setOpenPopup] = useState(false)

  const globalI18n = (global as any).i18n

  const onLanguageChanged = (lng: string): void => {
    setValue(lng)
    const resourceData = get(globalI18n, 'services.resourceStore.data', [])
    if (!isEmpty(resourceData)) {
      setLanguages(keys(resourceData))
    }
  }

  useEffect(() => {
    if (globalI18n && !skipListener) {
      onLanguageChanged(get(globalI18n, 'language'))
      globalI18n.on('languageChanged', onLanguageChanged)

      return () => {
        globalI18n.off('languageChanged', onLanguageChanged)
      }
    }
  }, [])

  const onChange = ({ value }): void => {
    if (openPopup) {
      setOpenPopup(false)
    }
    if (globalI18n) {
      globalI18n.changeLanguage(value)
    }

    if (typeof contextOnChange === 'function') {
      contextOnChange(value)
    }
  }

  const options = useMemo(
    () =>
      LANGUAGE_OPTIONS.filter((option) => {
        if (isEmpty(translatedLanguages)) {
          if (languages) {
            return languages.includes(option.value)
          }
          return contextLanguages && contextLanguages.includes(option.value)
        }
        return translatedLanguages.includes(option.value)
      }),
    [languages, contextLanguages, translatedLanguages],
  )

  if (enableBlandMode) {
    const selectedValue = find(
      options,
      (o) => o.value === (value || contextValue),
    )

    if (!selectedValue) return <></>
    const { label, value: valueSelected } = selectedValue
    return (
      <Popover
        as={DropdownWrapper}
        popover={
          <div>
            {options.map((item) => {
              return (
                <Dropdown.Item
                  as={DropdownItemWrapper}
                  className="py-2 px-1 d-flex align-items-center"
                  onClick={() => onChange(item)}
                  key={item.value}
                >
                  <i
                    className={`flag-icon flag-icon-${item.flag} ${
                      !hideCountryText ? 'mr-1' : ''
                    }`}
                    style={{ fontSize: flagSize }}
                  />
                  {hideCountryText ? null : (
                    <span>
                      {useShortCode ? upperCase(item.value) : item.label}
                    </span>
                  )}
                </Dropdown.Item>
              )
            })}
          </div>
        }
        on="click"
        className="p-0"
        open={openPopup}
        offset={[0, -30]}
        onClose={() => setOpenPopup(false)}
        hideOnScroll
      >
        <Button textOnly onClick={() => setOpenPopup(true)}>
          <i
            className={`flag-icon flag-icon-${
              find(LANGUAGE_OPTIONS, (o) => o.value === valueSelected)?.flag
            }`}
            style={{ fontSize: flagSize }}
          />
        </Button>
      </Popover>
    )
  }
  return (
    <LanguageSelectComponent
      flagSize={flagSize}
      value={value || contextValue}
      useShortCode={useShortCode}
      hideCountryText={hideCountryText}
      flagsOnly={flagsOnly}
      onChange={onChange}
      useDarkMode={useDarkMode}
      flipLeft={flipLeft}
      useMinWidth={useMinWidth}
    />
  )
}

export default LanguageSelect

export { LanguageContext }
